import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import { zoneAbbreviation } from 'src/components/Compliance/Appointments/CalendarView';
import {
  RenterBookingOptions,
  RenterCategoryOption,
  RenterSlot,
  RenterSlotDay,
  bookRenterAppointment,
  fetchRenterBookingOptions,
  fetchRenterSlots,
  notBookableMessage,
  rescheduleRenterAppointment,
  summarizeRenterUnits,
  toRenterApiError,
} from './useRenterAppointments';

dayjs.extend(utc);
dayjs.extend(timezone);

type Step = 'what' | 'when' | 'confirm';
const STEPS: { key: Step; label: string }[] = [
  { key: 'what', label: 'What' },
  { key: 'when', label: 'When' },
  { key: 'confirm', label: 'Confirm' },
];

const WINDOW_DAYS = 14;

const formatSlotTime = (iso: string, tz: string): string =>
  dayjs(iso).tz(tz).format('h:mm A');

/** Renter self-booking (ENG-3895). Route:
 * /renter/appointments/book/:certificationId[?reschedule=<id>&category=<id>]
 * Reschedule skips "What" (the type cannot change). */
const RenterBookingFlow: FC = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { certificationId: certParam } = useParams<{
    certificationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const certificationId = Number(certParam);
  const rescheduleId = searchParams.get('reschedule')
    ? Number(searchParams.get('reschedule'))
    : null;
  const presetCategory = searchParams.get('category')
    ? Number(searchParams.get('category'))
    : null;
  const isReschedule = Boolean(rescheduleId);

  const [options, setOptions] = useState<RenterBookingOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>(isReschedule ? 'when' : 'what');
  const [category, setCategory] = useState<RenterCategoryOption | null>(null);
  const [windowStart, setWindowStart] = useState<Dayjs>(() =>
    dayjs().startOf('day')
  );
  const [days, setDays] = useState<RenterSlotDay[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<RenterSlot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.APPOINTMENTS}`;
  const tz = options?.timezone ?? 'America/New_York';

  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    fetchRenterBookingOptions(certificationId, rescheduleId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setOptions(data);
        if (data) {
          // Booking is by invitation: `categories` is what the invite allows.
          // Rescheduling uses the existing appointment's own type first; it
          // must keep working after the invite is consumed.
          const reschedulePool = data.reschedule_categories?.length
            ? data.reschedule_categories
            : data.renter_bookable_categories ?? data.categories;
          const pool = isReschedule ? reschedulePool : data.categories;
          const preset = presetCategory
            ? pool.find((c) => c.id === presetCategory) ?? null
            : null;
          if (preset) {
            setCategory(preset);
          } else if (pool.length === 1) {
            setCategory(pool[0]);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOptionsError(
            'We could not load booking options. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [certificationId, presetCategory, isReschedule, rescheduleId]);

  const loadSlots = useCallback(async () => {
    if (!category) {
      return;
    }
    setSlotsLoading(true);
    setSlotsError(null);
    try {
      const response = await fetchRenterSlots({
        certificationId,
        categoryId: category.id,
        start: windowStart.format('YYYY-MM-DD'),
        end: windowStart.add(WINDOW_DAYS - 1, 'day').format('YYYY-MM-DD'),
        excludeAppointmentId: rescheduleId,
        rescheduleAppointmentId: rescheduleId,
      });
      setDays(response.days);
      setSelectedDay((current) =>
        current && response.days.some((d) => d.date === current)
          ? current
          : response.days[0]?.date ?? null
      );
    } catch {
      setSlotsError('We could not load open times. Please try again.');
      setDays([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [category, certificationId, windowStart, rescheduleId]);

  useEffect(() => {
    if (step === 'when') {
      loadSlots();
    }
  }, [step, loadSlots]);

  const selectedDaySlots = useMemo(
    () => days.find((d) => d.date === selectedDay)?.slots ?? [],
    [days, selectedDay]
  );

  // Slots arrive merged across every host. Show the time alone, and add the
  // host only where two hosts offer the identical time so the renter can tell
  // the otherwise-identical chips apart.
  const slotChips = useMemo(() => {
    const perTime = new Map<string, number>();
    selectedDaySlots.forEach((s) => {
      perTime.set(s.start_at, (perTime.get(s.start_at) ?? 0) + 1);
    });
    return selectedDaySlots.map((s) => ({
      slot: s,
      label:
        (perTime.get(s.start_at) ?? 0) > 1
          ? `${formatSlotTime(s.start_at, tz)} · ${s.host_name}`
          : formatSlotTime(s.start_at, tz),
    }));
  }, [selectedDaySlots, tz]);

  const unitsSummary = useMemo(
    () => summarizeRenterUnits(options?.units),
    [options?.units]
  );

  const visibleSteps = isReschedule
    ? STEPS.filter((s) => s.key !== 'what')
    : STEPS;

  const goBack = useCallback(() => {
    const order: Step[] = isReschedule
      ? ['when', 'confirm']
      : ['what', 'when', 'confirm'];
    const i = order.indexOf(step);
    if (i <= 0) {
      navigate(backRoute);
    } else {
      setStep(order[i - 1]);
    }
  }, [step, isReschedule, navigate, backRoute]);

  const handleSubmit = useCallback(async () => {
    if (!slot || !category) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isReschedule && rescheduleId) {
        await rescheduleRenterAppointment(rescheduleId, {
          new_start_at: slot.start_at,
        });
      } else {
        await bookRenterAppointment({
          certification: certificationId,
          category: category.id,
          start_at: slot.start_at,
        });
      }
      navigate(`${backRoute}?booked=1`);
    } catch (err: unknown) {
      const apiError = toRenterApiError(err);
      if (
        apiError.code === 'slot_taken' ||
        apiError.code === 'outside_availability'
      ) {
        setSubmitError('That time was just taken. Please pick another.');
        setSlot(null);
        setStep('when');
        loadSlots();
      } else if (apiError.code === 'cutoff_passed') {
        setSubmitError(
          'It is too close to your appointment to change it online. Please call the office.'
        );
      } else {
        setSubmitError(apiError.detail);
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    slot,
    category,
    isReschedule,
    rescheduleId,
    certificationId,
    navigate,
    backRoute,
    loadSlots,
  ]);

  const fmtTime = (iso: string): string => formatSlotTime(iso, tz);
  const zoneAbbr = zoneAbbreviation(tz);

  if (optionsLoading) {
    return (
      <Box>
        <PageHeader
          title={isReschedule ? 'Pick a new time' : 'Book a visit'}
          icon={<EventAvailableIcon />}
          backRoute={backRoute}
        />
        <Box py={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Rescheduling only needs a resolved type; a fresh booking needs the invite.
  const canProceed = isReschedule
    ? Boolean(options && category)
    : Boolean(options?.bookable);

  if (optionsError || !options || !canProceed) {
    return (
      <Box>
        <PageHeader
          title={isReschedule ? 'Pick a new time' : 'Book a visit'}
          icon={<EventAvailableIcon />}
          backRoute={backRoute}
        />
        <Container maxWidth="sm" sx={{ py: 3 }}>
          <Alert severity="info" data-testid="booking-not-open">
            {optionsError ??
              (isReschedule
                ? 'This appointment cannot be moved online right now. Please call the office.'
                : notBookableMessage(options))}
          </Alert>
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={() => navigate(backRoute)}
          >
            Back
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={isReschedule ? 'Pick a new time' : 'Book a visit'}
        icon={<EventAvailableIcon />}
        backRoute={backRoute}
        subtitle={options.property_name}
      />
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Stack spacing={3}>
          <Stepper
            activeStep={visibleSteps.findIndex((s) => s.key === step)}
            alternativeLabel={!isPhone}
            sx={isPhone ? { '& .MuiStepLabel-label': { fontSize: 12 } } : undefined}
          >
            {visibleSteps.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {submitError && <Alert severity="warning">{submitError}</Alert>}

          {step === 'what' && (
            <Stack spacing={1.5}>
              <Typography variant="h6">What kind of visit?</Typography>
              {options.categories.map((c) => (
                <Card
                  key={c.id}
                  variant="outlined"
                  sx={{
                    borderColor:
                      category?.id === c.id ? 'primary.main' : 'divider',
                    borderWidth: category?.id === c.id ? 2 : 1,
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      setCategory(c);
                      setStep('when');
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="subtitle1">{c.name}</Typography>
                        <Chip
                          size="small"
                          label={`${c.duration_minutes} min`}
                        />
                      </Stack>
                      {c.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          {c.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}

          {step === 'when' && (
            <Stack spacing={2}>
              <Typography variant="h6">
                Pick a time that works for you.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Times are shown in {zoneAbbr}.
                {category
                  ? ` Each ${category.name.toLowerCase()} is ${
                      category.duration_minutes
                    } minutes.`
                  : ''}
              </Typography>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  disabled={windowStart.isSame(dayjs().startOf('day'), 'day')}
                  onClick={() =>
                    setWindowStart((w) => w.subtract(WINDOW_DAYS, 'day'))
                  }
                >
                  Earlier
                </Button>
                <Typography
                  variant="body2"
                  sx={{ flexGrow: 1, textAlign: 'center' }}
                >
                  {windowStart.format('MMM D')} –{' '}
                  {windowStart.add(WINDOW_DAYS - 1, 'day').format('MMM D')}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={() =>
                    setWindowStart((w) => w.add(WINDOW_DAYS, 'day'))
                  }
                >
                  Later
                </Button>
              </Stack>

              {slotsError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={loadSlots}>
                      Try again
                    </Button>
                  }
                >
                  {slotsError}
                </Alert>
              )}

              {slotsLoading ? (
                <Box py={4} display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : days.length === 0 ? (
                <Alert severity="info">
                  No open times in these two weeks. Try “Later”, or call the
                  office.
                </Alert>
              ) : (
                <>
                  <Box
                    sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}
                  >
                    {days.map((d) => {
                      const day = dayjs(d.date);
                      const active = d.date === selectedDay;
                      return (
                        <Box
                          key={d.date}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setSelectedDay(d.date);
                            setSlot(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setSelectedDay(d.date);
                            }
                          }}
                          sx={{
                            minWidth: 64,
                            px: 1,
                            py: 1,
                            textAlign: 'center',
                            borderRadius: 2,
                            cursor: 'pointer',
                            bgcolor: active
                              ? 'primary.main'
                              : alpha(theme.palette.primary.main, 0.08),
                            color: active
                              ? 'primary.contrastText'
                              : 'text.primary',
                          }}
                        >
                          <Typography variant="caption" display="block">
                            {day.format('ddd')}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            lineHeight={1.1}
                          >
                            {day.format('D')}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {d.slots.length} open
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: isPhone
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'repeat(auto-fill, minmax(104px, 1fr))',
                      gap: 1,
                    }}
                  >
                    {slotChips.map(({ slot: s, label }) => {
                      const active =
                        slot?.start_at === s.start_at &&
                        slot?.host_id === s.host_id;
                      return (
                        <Button
                          key={`${s.start_at}-${s.host_id}`}
                          variant={active ? 'contained' : 'outlined'}
                          onClick={() => setSlot(s)}
                          sx={{
                            textTransform: 'none',
                            px: 1,
                            lineHeight: 1.2,
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                </>
              )}

              <Stack
                direction={isPhone ? 'column' : 'row'}
                spacing={1}
                justifyContent="space-between"
              >
                <Button color="inherit" onClick={goBack} fullWidth={isPhone}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  disabled={!slot}
                  fullWidth={isPhone}
                  onClick={() => setStep('confirm')}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}

          {step === 'confirm' && slot && category && (
            <Stack spacing={2}>
              <Typography variant="h6">Does this look right?</Typography>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Row
                      label="Visit"
                      value={category.name}
                      stacked={isPhone}
                    />
                    <Row
                      label="When"
                      stacked={isPhone}
                      value={`${dayjs(slot.start_at)
                        .tz(tz)
                        .format('dddd, MM/DD/YYYY')} at ${fmtTime(
                        slot.start_at
                      )} ${zoneAbbr}`}
                    />
                    <Row
                      label="With"
                      value={slot.host_name}
                      stacked={isPhone}
                    />
                    <Row
                      label="Where"
                      value={options.property_name}
                      stacked={isPhone}
                    />
                    {unitsSummary.labels && (
                      <Row
                        label="Units"
                        value={unitsSummary.labels}
                        extra={unitsSummary.extra}
                        stacked={isPhone}
                      />
                    )}
                    <Row
                      label="How long"
                      stacked={isPhone}
                      value={`${category.duration_minutes} minutes`}
                    />
                  </Stack>
                </CardContent>
              </Card>
              {category.booking_instructions && (
                <Alert severity="info" icon={false}>
                  {category.booking_instructions}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                We&apos;ll text and email you a reminder. You can cancel or move
                it online up to {category.cancel_cutoff_hours} hour
                {category.cancel_cutoff_hours === 1 ? '' : 's'} before.
              </Typography>
              <Button
                variant="outlined"
                fullWidth={isPhone}
                onClick={() => navigate(backRoute)}
                sx={isPhone ? undefined : { alignSelf: 'flex-start' }}
              >
                View my appointments
              </Button>
              <Stack
                direction={isPhone ? 'column' : 'row'}
                spacing={1}
                justifyContent="space-between"
              >
                <Button
                  color="inherit"
                  onClick={goBack}
                  disabled={submitting}
                  fullWidth={isPhone}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  fullWidth={isPhone}
                >
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isReschedule ? (
                    'Move Appointment'
                  ) : (
                    'Book Appointment'
                  )}
                </Button>
              </Stack>
            </Stack>
          )}

          {step === 'what' && (
            <Button
              color="inherit"
              onClick={goBack}
              sx={{ alignSelf: 'flex-start' }}
            >
              Back
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

const Row: FC<{
  label: string;
  value: string;
  extra?: number;
  stacked?: boolean;
}> = ({ label, value, extra = 0, stacked = false }) => (
  <Stack
    direction={stacked ? 'column' : 'row'}
    spacing={stacked ? 0.25 : 2}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={stacked ? undefined : { width: 80, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
      {extra > 0 && <Chip size="small" label={`+${extra}`} />}
    </Stack>
  </Stack>
);

export default RenterBookingFlow;
